/**
 * EvidenceSection — "Proof it worked."
 *
 * Lists evidence grouped by milestone (and a CAP-level bucket for evidence
 * that isn't tied to a single step).
 *
 * Two add flows:
 *   - "+ Add note" — inline textarea, posts {kind:'note', note_body}
 *   - "+ Add link" — inline url + description, posts {kind:'link', link_url, description}
 *
 * File upload is reserved for Sprint C; we render a faint disabled pill so
 * users can see it's coming without being misled.
 */

import React, { useState } from 'react';
import SectionCard from './SectionCard';
import PhiFriendlyNote from '../../../components/PhiFriendlyNote.jsx';

const API = process.env.REACT_APP_API_URL;

function EvidenceItem({ ev }) {
  if (ev.kind === 'file') {
    const downloadUrl = `${API}/api/pcap/evidence/${ev.id}/download`;
    return (
      <li className="text-sm text-slate-800">
        <span aria-hidden>📎</span>{' '}
        <a href={downloadUrl} className="underline text-slate-700 hover:text-slate-900" target="_blank" rel="noreferrer">
          {ev.file_name || 'File'}
        </a>
        {ev.description && <span className="text-slate-500 text-[12px] ml-2">— {ev.description}</span>}
      </li>
    );
  }
  if (ev.kind === 'note') {
    return (
      <li className="text-sm text-slate-800">
        <span aria-hidden>📝</span> <span className="whitespace-pre-wrap">{ev.note_body}</span>
      </li>
    );
  }
  if (ev.kind === 'link') {
    return (
      <li className="text-sm text-slate-800">
        <span aria-hidden>🔗</span>{' '}
        <a href={ev.link_url} className="underline text-slate-700 hover:text-slate-900" target="_blank" rel="noreferrer">
          {ev.description || ev.link_url}
        </a>
      </li>
    );
  }
  return null;
}

function AddEvidenceForms({ capId, milestoneId, onCreated }) {
  const [mode, setMode] = useState(null); // 'note' | 'link' | 'file' | null
  const [noteBody, setNoteBody] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDesc, setLinkDesc] = useState('');
  const [fileInput, setFileInput] = useState(null);
  const [fileDesc, setFileDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const post = async (body) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${capId}/evidence`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, milestone_id: milestoneId || null }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not save');
      setMode(null);
      setNoteBody('');
      setLinkUrl('');
      setLinkDesc('');
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitNote = (e) => {
    e.preventDefault();
    if (!noteBody.trim()) return;
    post({ kind: 'note', note_body: noteBody.trim() });
  };
  const submitLink = (e) => {
    e.preventDefault();
    if (!linkUrl.trim()) return;
    post({ kind: 'link', link_url: linkUrl.trim(), description: linkDesc.trim() || null });
  };
  const submitFile = async (e) => {
    e.preventDefault();
    if (!fileInput) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', fileInput);
      if (milestoneId) fd.append('milestone_id', milestoneId);
      if (fileDesc.trim()) fd.append('description', fileDesc.trim());
      const res = await fetch(`${API}/api/pcap/caps/${capId}/evidence/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Upload failed');
      setMode(null);
      setFileInput(null);
      setFileDesc('');
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      {!mode && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setMode('note')}
            className="text-[12px] text-slate-700 ring-1 ring-slate-200 rounded-md px-2 py-1 hover:bg-slate-50"
          >
            + Add note
          </button>
          <button
            type="button"
            onClick={() => setMode('link')}
            className="text-[12px] text-slate-700 ring-1 ring-slate-200 rounded-md px-2 py-1 hover:bg-slate-50"
          >
            + Add link
          </button>
          <button
            type="button"
            onClick={() => setMode('file')}
            className="text-[12px] text-slate-700 ring-1 ring-slate-200 rounded-md px-2 py-1 hover:bg-slate-50"
          >
            + Upload file
          </button>
        </div>
      )}

      {mode === 'file' && (
        <form onSubmit={submitFile} className="rounded-md ring-1 ring-slate-200 bg-slate-50 p-2 space-y-2">
          <PhiFriendlyNote variant="upload" />
          <input
            autoFocus
            type="file"
            onChange={e => setFileInput(e.target.files?.[0] || null)}
            className="block w-full text-sm text-slate-800 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:text-[12px] file:font-medium hover:file:bg-slate-800"
          />
          <input
            value={fileDesc}
            onChange={e => setFileDesc(e.target.value)}
            placeholder="What is this file? (optional)"
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
          <p className="text-[11px] text-slate-500">Up to 25 MB. Executables and scripts are blocked.</p>
          {error && <p className="text-[12px] text-red-700">{error}</p>}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy || !fileInput}
              className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? 'Uploading…' : 'Upload'}
            </button>
            <button
              type="button"
              onClick={() => { setMode(null); setError(null); setFileInput(null); setFileDesc(''); }}
              className="px-3 py-1.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {mode === 'note' && (
        <form onSubmit={submitNote} className="rounded-md ring-1 ring-slate-200 bg-slate-50 p-2 space-y-2">
          <textarea
            autoFocus
            value={noteBody}
            onChange={e => setNoteBody(e.target.value)}
            rows={3}
            placeholder="Write a short note describing what this evidence shows."
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
          {error && <p className="text-[12px] text-red-700">{error}</p>}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy || !noteBody.trim()}
              className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save note'}
            </button>
            <button
              type="button"
              onClick={() => { setMode(null); setError(null); }}
              className="px-3 py-1.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {mode === 'link' && (
        <form onSubmit={submitLink} className="rounded-md ring-1 ring-slate-200 bg-slate-50 p-2 space-y-2">
          <input
            autoFocus
            type="url"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            placeholder="https://…"
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            required
          />
          <input
            value={linkDesc}
            onChange={e => setLinkDesc(e.target.value)}
            placeholder="What is this? (optional)"
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
          {error && <p className="text-[12px] text-red-700">{error}</p>}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy || !linkUrl.trim()}
              className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save link'}
            </button>
            <button
              type="button"
              onClick={() => { setMode(null); setError(null); }}
              className="px-3 py-1.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function EvidenceSection({ data, onRefresh }) {
  const cap = data.cap;
  const milestones = data.milestones || [];
  // Source documents render in their own section at the top of the page;
  // exclude them here so the Evidence list shows only files/notes/links
  // that prove remediation worked.
  const evidence = (data.evidence || []).filter(e => e.kind !== 'source');

  // Group evidence by milestone_id (null = CAP-level).
  const byMilestone = new Map();
  byMilestone.set(null, []);
  for (const m of milestones) byMilestone.set(m.id, []);
  for (const e of evidence) {
    const key = e.milestone_id || null;
    if (!byMilestone.has(key)) byMilestone.set(key, []);
    byMilestone.get(key).push(e);
  }

  return (
    <SectionCard
      title="Evidence"
      hint="Files, notes, and links that show the remediation did what it was supposed to do."
    >
      {/* Per-milestone groups */}
      {milestones.length > 0 && (
        <div className="space-y-4">
          {milestones.map(m => {
            const items = byMilestone.get(m.id) || [];
            return (
              <div key={m.id} className="rounded-lg ring-1 ring-slate-100 p-3">
                <p className="text-[12px] font-medium text-slate-700 mb-1">
                  {m.description || '(unnamed step)'}
                </p>
                {items.length === 0 ? (
                  <p className="text-[12px] text-slate-400 italic">No evidence for this step yet.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {items.map(e => <EvidenceItem key={e.id} ev={e} />)}
                  </ul>
                )}
                <AddEvidenceForms capId={cap.id} milestoneId={m.id} onCreated={onRefresh} />
              </div>
            );
          })}
        </div>
      )}

      {/* CAP-level bucket */}
      <div className={milestones.length > 0 ? 'mt-4 rounded-lg ring-1 ring-slate-100 p-3' : ''}>
        {milestones.length > 0 && (
          <p className="text-[12px] font-medium text-slate-700 mb-1">
            For the CAP overall
          </p>
        )}
        {(byMilestone.get(null) || []).length === 0 ? (
          <p className="text-[12px] text-slate-400 italic">
            {milestones.length === 0 ? 'No evidence yet.' : 'No CAP-level evidence yet.'}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {byMilestone.get(null).map(e => <EvidenceItem key={e.id} ev={e} />)}
          </ul>
        )}
        <AddEvidenceForms capId={cap.id} milestoneId={null} onCreated={onRefresh} />
      </div>
    </SectionCard>
  );
}
